import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Button, TreeItem, Tree } from '@picsio/ui';

import useInboxesTreeLogic from '../../helpers/components/hooks/useInboxesTreeLogic';
import l18n from '../../shared/strings';
import helpersLink from '../Billing/Overview/Features/constants/helpersLink';
import UpgradePlanBlock from '../UpgradePlanBlock';
import TreePlaceholder from '../TreePlaceholder';
import Icon from '../Icon';
import styles from './styles.module.scss';
import Logger from '../../services/Logger';

const Inboxes = (props) => {
  const { destroy } = props;
  const { inboxes: isInboxesAllowed } = useSelector((state) => state.user.subscriptionFeatures);
  const isBusy = useSelector((state) => state.inboxes.isBusy);
  const isCreatingInbox = useSelector((state) => state.inboxes.isCreatingInbox);

  const treePlaceholderRef = useRef(null);

  const {
    isValidNewName,
    addInProgress,
    newCollectionName,
    defaultNewCollectionName,
    editValidation,
    handleClickCreate,
    handleCreate,
    handleCreateCancel,
    handleNodeSelect,
    renderNode,
    inboxes,
    filtredInboxes,
    selected,
  } = useInboxesTreeLogic({ initiatedFrom: 'Inbox modal' });

  const onCreate = useCallback(() => {
    treePlaceholderRef.current?.addHandler?.();
  }, []);

  const onItemAction = useCallback((action, options = {}) => {
    const initiatedFrom = 'Inbox modal';
    if (action === 'settings') {
      destroy();
    }
    if (action === 'copyLink') {
      Logger.log('User', 'InboxPanelCopyInboxLinkClicked', { initiatedFrom, inboxId: options?.inboxId });
    }
    if (action === 'rename') {
      Logger.log('User', 'InboxesPanelRenameInbox', { initiatedFrom, inboxId: options.inboxId });
    }
    if (action === 'delete') {
      Logger.log('User', 'InboxesPanelDeleteInboxModalShown', { initiatedFrom, inboxId: options.inboxId });
    }
  }, [destroy]);

  return (
    <div className={cn('simpleDialog', styles.inboxes)}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle f-w-3">
            {l18n.DIALOGS.INBOX.TITLE}
          </span>
          <span className="simpleDialogBtnCross" onClick={destroy} role="presentation">
            <Icon name="close" />
          </span>
        </div>
        <Choose>
          <When condition={!isInboxesAllowed}>
            <UpgradePlanBlock
              title={l18n.UPGRADE_PLAN_BLOCK.inboxes.title}
              description={l18n.UPGRADE_PLAN_BLOCK.inboxes.description}
              helperText={l18n.UPGRADE_PLAN_BLOCK.inboxes.helperText}
              helperLink={helpersLink.inboxesLimit}
              featureName="inboxes"
              imageSrc="https://assets.pics.io/img/billing/upgrade_plan_inboxes.png"
              redirectToBilling
            />
          </When>

          <Otherwise>
            <div className="simpleDialogContent">
              <div className="simpleDialogDescription">
                <div className="simpleDialogDescription__text f-s-16 f-w-3">
                  {l18n.DIALOGS.INBOX.TEXT}
                </div>
              </div>
              <Choose>
                <When condition={inboxes.length === 0}>
                  <TreePlaceholder
                    title=""
                    ref={treePlaceholderRef}
                    buttonId="createInbox"
                    isShownIcon={false}
                    isShownCreateButton={false}
                    className={styles.treePlaceholder}
                    add={handleCreate}
                    validate={editValidation}
                    error={isValidNewName}
                    isBusy={isBusy}
                    isFeatureAllowed={isInboxesAllowed}
                  />
                </When>
                <Otherwise>
                  <div className={styles.inboxesTree}>
                    <If condition={addInProgress || isCreatingInbox}>
                      <TreeItem
                        root
                        nodeId="Root"
                        classNames={styles.inboxesListItem}
                        style={{ listStyle: 'none' }}
                        level={0}
                        onClick={null}
                        editable={false}
                        iseditModeOnDoubleClick={false}
                        editValidation={editValidation}
                        adding={addInProgress}
                        newName={newCollectionName}
                        defaultNewName={defaultNewCollectionName}
                        onAdd={handleCreate}
                        onAddCancel={handleCreateCancel}
                        canDrop={false}
                      />
                    </If>
                    <Tree
                      nodes={filtredInboxes || inboxes}
                      renderNode={(nodeProps) => renderNode({ ...nodeProps, onAction: onItemAction })}
                      selected={selected}
                      onNodeSelect={handleNodeSelect}
                      controlled
                    />
                  </div>
                </Otherwise>
              </Choose>
            </div>
            <div className="simpleDialogFooter">
              <Button
                color="secondary"
                size="md"
                variant="contained"
                onClick={destroy}
              >
                {l18n.DIALOGS.INBOX.CANCEL_TEXT}
              </Button>
              <Button
                color="primary"
                size="md"
                variant="contained"
                disabled={addInProgress || isCreatingInbox}
                onClick={inboxes.length === 0 ? onCreate : handleClickCreate}
              >
                {l18n.DIALOGS.INBOX.OK_TEXT}
              </Button>
            </div>
          </Otherwise>
        </Choose>
      </div>
    </div>
  );
};

Inboxes.propTypes = {
  destroy: PropTypes.func,
};

Inboxes.defaultProps = {
  destroy: () => {},
};

export default Inboxes;
